import { Children, type PropsWithChildren, useCallback } from 'react';
import { useRouter } from 'expo-router';
import { StyleSheet, View } from 'react-native';
import { type SharedValue } from 'react-native-reanimated';

import { useResponsive } from '@sg/garnish';

import { MenuHeader } from '@order/components-menu';
import { useDeliveryDisclosure } from '@order/shared-hooks';

import {
  type MenuContentDeliveryDetailsData,
  type MenuContentOrderChannel,
  type MenuContentRestaurantData,
} from '../../MenuContent.types';
import { MenuContentMoreInfoModal, useMenuContentMoreInfoModal } from '../MenuContentMoreInfoModal';
import { MenuContentHeaderDetails } from './components';

// ─────────────────────────────────────────────────────────────────────────────

export const MenuContentHeader = (props: Props) => {
  const {
    restaurantData,
    locationName,
    orderChannel,
    deliveryDetailsData,
    children,
    menuGridScrollOffsetSV,
    compact,
    dataSet,
    changeLocation,
    onContactUs,
    shouldHideDeliveryFeeDisclosure,
  } = props;

  const hasChildren = Children.count(children) > 0;

  const { flexMessage, showDeliveryFeeDisclosure, hours, utcOffset } = restaurantData;

  // ───────────────────────────────────────────────────────────────────────────

  const router = useRouter();
  const { minWidth } = useResponsive();

  const moreInfoModal = useMenuContentMoreInfoModal();
  const { deliveryDisclosure } = useDeliveryDisclosure({ restaurantDeliveryFee: deliveryDetailsData?.deliveryFee }); // prettier-ignore

  // ─── Helpers ───────────────────────────────────────────────────────────────

  const openContactUs = useCallback(() => {
    router.navigate('/chat');
  }, [router]);

  const handleContactUs = useCallback(() => {
    onContactUs?.();
    moreInfoModal.hideMoreInfoModal();

    // NOTE: To ensure modal is completely hidden before navigating.
    requestIdleCallback(() => {
      openContactUs();
    });
  }, [moreInfoModal, onContactUs, openContactUs]);

  const handleChangeLocation = useCallback(() => {
    if (changeLocation) {
      changeLocation();

      return;
    }

    router.navigate('/location-selection');
  }, [changeLocation, router]);

  // ─── Flags ───────────────────────────────────────────────────────────────────

  const isDelivery = orderChannel === 'delivery';
  const isOutpost = orderChannel === 'outpost';

  const hasDeliveryDisclosure = Boolean(deliveryDisclosure);
  const hasFlexMessage = Boolean(flexMessage);
  const shouldRenderMoreInfoModal = !isDelivery;

  const shouldRenderFlexMessage = !isDelivery && minWidth.isSM && hasFlexMessage; // prettier-ignore
  const shouldRenderDeliveryFeeDisclosure = !shouldHideDeliveryFeeDisclosure && isDelivery && showDeliveryFeeDisclosure && hasDeliveryDisclosure; // prettier-ignore
  const shouldRenderHeaderNotices = shouldRenderDeliveryFeeDisclosure || shouldRenderFlexMessage; // prettier-ignore
  const shouldRenderWithoutTopSpacing = minWidth.isSM;

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <>
      <MenuHeader.Container
        menuGridScrollOffsetSV={menuGridScrollOffsetSV}
        compact={compact}
        withoutTopSpacing={shouldRenderWithoutTopSpacing}
      >
        {minWidth.isSM ? (
          <MenuContentHeaderDetails
            locationName={locationName}
            orderChannel={orderChannel}
            deliveryDetailsData={deliveryDetailsData}
            hours={hours}
            utcOffset={utcOffset}
            dataSet={dataSet}
            shouldRenderMoreInfoButton={shouldRenderMoreInfoModal}
            onChangeLocationPress={handleChangeLocation}
            onMoreInfoPress={moreInfoModal.showMoreInfoModal}
          />
        ) : null}

        {shouldRenderHeaderNotices ? (
          <MenuHeader.AlertsContainer>
            {shouldRenderFlexMessage ? <MenuHeader.Alert>{flexMessage}</MenuHeader.Alert> : null}

            {shouldRenderDeliveryFeeDisclosure ? (
              <MenuHeader.Alert sizeMatch={['14']}>{deliveryDisclosure}</MenuHeader.Alert>
            ) : null}
          </MenuHeader.AlertsContainer>
        ) : null}

        {hasChildren ? <View style={styles.contentContainer}>{children}</View> : null}
      </MenuHeader.Container>

      {shouldRenderMoreInfoModal ? (
        <MenuContentMoreInfoModal
          isVisible={moreInfoModal.isMoreInfoModalVisible}
          onClose={moreInfoModal.hideMoreInfoModal}
          locationName={locationName}
          restaurant={restaurantData}
          orderChannel={orderChannel}
          onContactUs={isOutpost ? handleContactUs : undefined} // NOTE: Outpost prefers to use the chatbot instead of the phone number.
        />
      ) : null}
    </>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  contentContainer: {},
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = PropsWithChildren<{
  restaurantData: MenuContentRestaurantData;
  locationName: string;
  orderChannel: MenuContentOrderChannel;
  deliveryDetailsData: MenuContentDeliveryDetailsData | undefined | null;
  menuGridScrollOffsetSV?: SharedValue<number>;
  compact?: boolean;
  dataSet?: Record<string, string>;
  changeLocation?: () => void;
  onContactUs?: () => void;
  shouldHideDeliveryFeeDisclosure?: boolean;
}>;
